import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from '../hooks/useTranslation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FamilySignup = () => {
  const { t, language } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    birthday: '',
    anniversary: '',
    favoritePrograms: '',
    feedback: '',
    country: '',
    city: ''
  });
  
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${BACKEND_URL}/api/audience/family/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const data = await response.json();
        setStatus({ 
          type: 'success', 
          message: language === 'fr' 
            ? 'Merci de rejoindre notre famille Kioo Radio ! Nous vous contacterons bientôt.' 
            : 'Thank you for joining the Kioo Radio family! We\'ll be in touch soon.'
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          birthday: '',
          anniversary: '',
          favoritePrograms: '',
          feedback: '',
          country: '',
          city: ''
        });
      } else {
        const errorData = await response.json();
        setStatus({ 
          type: 'error', 
          message: errorData.detail || (language === 'fr' ? 'Une erreur s\'est produite' : 'An error occurred')
        });
      }
    } catch (error) {
      console.error('Signup error:', error);
      setStatus({ 
        type: 'error', 
        message: language === 'fr' 
          ? 'Erreur de connexion. Veuillez réessayer.' 
          : 'Connection error. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{language === 'fr' ? 'Rejoignez Notre Famille' : 'Join Our Family'} - Kioo Radio 98.1FM</title>
        <meta name="description" content={language === 'fr' ? 'Rejoignez la famille des auditeurs de Kioo Radio' : 'Join the Kioo Radio listener family'} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-kioo-light via-white to-green-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-kioo-primary to-kioo-secondary rounded-full mb-4">
              <span className="text-4xl">❤️</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Rejoignez Notre Famille' : 'Join Our Family'}
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {language === 'fr' 
                ? 'Devenez membre de notre communauté d\'auditeurs. Partagez vos anniversaires, préférences et feedbacks avec nous !'
                : 'Become part of our listener community. Share your celebrations, preferences, and feedback with us!'}
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  {language === 'fr' ? 'Nom Complet' : 'Full Name'} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent transition"
                  placeholder={language === 'fr' ? 'Entrez votre nom complet' : 'Enter your full name'}
                />
              </div>

              {/* Email & Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    {language === 'fr' ? 'Email' : 'Email'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent transition"
                    placeholder={language === 'fr' ? 'votre@email.com' : 'your@email.com'}
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    {language === 'fr' ? 'Téléphone' : 'Phone'}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent transition"
                    placeholder={language === 'fr' ? '+231...' : '+231...'}
                  />
                </div>
              </div>

              {/* Country & City */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                    {language === 'fr' ? 'Pays' : 'Country'}
                  </label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent transition"
                  >
                    <option value="">{language === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                    <option value="Liberia">Liberia</option>
                    <option value="Sierra Leone">Sierra Leone</option>
                    <option value="Guinea">Guinea</option>
                    <option value="Other">{language === 'fr' ? 'Autre' : 'Other'}</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                    {language === 'fr' ? 'Ville' : 'City'}
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent transition"
                    placeholder={language === 'fr' ? 'Votre ville' : 'Your city'}
                  />
                </div>
              </div>

              {/* Birthday & Anniversary */}
              <div className="bg-green-50 p-6 rounded-xl space-y-4 border border-green-100">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">🎉</span>
                  <h3 className="text-lg font-semibold text-kioo-dark">
                    {language === 'fr' ? 'Célébrations' : 'Celebrations'}
                  </h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  {language === 'fr' 
                    ? 'Partagez vos dates spéciales pour recevoir des souhaits personnalisés à l\'antenne !'
                    : 'Share your special dates to receive personalized on-air greetings!'}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="birthday" className="block text-sm font-medium text-gray-700 mb-2">
                      {language === 'fr' ? 'Anniversaire (Date de naissance)' : 'Birthday'}
                    </label>
                    <input
                      type="date"
                      id="birthday"
                      name="birthday"
                      value={formData.birthday}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent transition"
                    />
                  </div>
                  <div>
                    <label htmlFor="anniversary" className="block text-sm font-medium text-gray-700 mb-2">
                      {language === 'fr' ? 'Anniversaire de Mariage' : 'Wedding Anniversary'}
                    </label>
                    <input
                      type="date"
                      id="anniversary"
                      name="anniversary"
                      value={formData.anniversary}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent transition"
                    />
                  </div>
                </div>
              </div>

              {/* Favorite Programs */}
              <div>
                <label htmlFor="favoritePrograms" className="block text-sm font-medium text-gray-700 mb-2">
                  {language === 'fr' ? 'Programmes Préférés' : 'Favorite Programs'}
                </label>
                <textarea
                  id="favoritePrograms"
                  name="favoritePrograms"
                  value={formData.favoritePrograms}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent transition"
                  placeholder={language === 'fr' ? 'Quels sont vos programmes préférés sur Kioo Radio ?' : 'What are your favorite programs on Kioo Radio?'}
                />
              </div>

              {/* Feedback */}
              <div>
                <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-2">
                  {language === 'fr' ? 'Commentaires & Suggestions' : 'Feedback & Suggestions'}
                </label>
                <textarea
                  id="feedback"
                  name="feedback"
                  value={formData.feedback}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent transition"
                  placeholder={language === 'fr' ? 'Partagez vos commentaires, suggestions ou demandes de prière...' : 'Share your feedback, suggestions, or prayer requests...'}
                />
              </div>

              {/* Status Message */}
              {status.message && (
                <div className={`p-4 rounded-lg ${status.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{status.type === 'success' ? '✅' : '⚠️'}</span>
                    <p>{status.message}</p>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-kioo-primary to-kioo-accent hover:from-kioo-secondary hover:to-kioo-primary text-white font-semibold py-4 px-6 rounded-lg transition duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isSubmitting 
                  ? (language === 'fr' ? '⏳ Envoi en cours...' : '⏳ Submitting...') 
                  : (language === 'fr' ? '💝 Rejoindre la Famille' : '💝 Join the Family')}
              </button>
            </form>
          </div>

          {/* Footer Note */}
          <div className="mt-8 text-center text-sm text-gray-600">
            <p>
              {language === 'fr' 
                ? '🔒 Vos informations sont sécurisées et ne seront jamais partagées avec des tiers.'
                : '🔒 Your information is secure and will never be shared with third parties.'}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default FamilySignup;
